import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { CheckCircle, XCircle } from 'lucide-react'

/* ─── Sample data ─── */
const GOOD_HEADERS = ['Date', 'Offer ID', 'Clicks', 'Conversions', 'Conv. Rate']
const GOOD_ROWS = [
  ['2024-01-15', 'offer_123', '1,250', '45', '3.6%'],
  ['2024-01-15', 'offer_456', '890', '12', '1.3%'],
  ['2024-01-16', 'offer_123', '1,100', '38', '3.5%'],
  ['2024-01-16', 'offer_456', '950', '15', '1.6%'],
  ['2024-01-17', 'offer_123', '1,300', '52', '4.0%'],
]

const BAD_HEADERS = ['Date', 'Offer Name', 'Clicks', 'Signups', 'Sales', 'Notes']
const BAD_CELLS = [
  ['Clicks Report', '', '', '', '', ''],
  ['', '', '', '', '', ''],
  ['Date', 'Offer Name', 'Clicks', 'Signups', 'Sales', 'Notes'],
  ['Jan 15', 'Zippi Home Loan', '1250', '45', '', 'Good day!'],
  ['2024-01-15', 'NovaPay credit card', '890', '', '12', ''],
  ['', 'Zippi home loan', '1,100', '38', '', ''],
  ['16 Jan 2024', '', '950', '', '15', 'NovaPay CC (forgot to put in col B)'],
  ['15/01/24', 'BrightSave Term Deposit', '720', '22', '', ''],
  ['TOTAL', '', '4,910', '', '', ''],
  ['2024-01-17', 'Zippi  Home Loan', '1,300', '52', '', ''],
]

const QUIZ = [
  {
    question: 'In a well-structured table, what goes in row 1?',
    options: [
      'The title of the spreadsheet',
      'Headers / column names',
      'The first row of data',
      'A summary or total row',
    ],
    correctIndex: 1,
    explanation: 'Row 1 should always contain your column headers. No title rows, no blanks above. Formulas and filters expect headers in row 1.',
  },
  {
    question: 'What is a "record" in a table?',
    options: [
      'A column containing one type of data',
      'A single row containing all information about one item',
      'The header row',
      'A formula that calculates a value',
    ],
    correctIndex: 1,
    explanation: 'Each row is one record -- all the data about one specific item (e.g., one offer on one date). Think of it as one observation or one entry.',
  },
  {
    question: 'Why are merged cells bad for data?',
    options: [
      'They make the sheet look ugly',
      'They use more memory',
      'Formulas can\'t reliably reference merged cells',
      'Google Sheets doesn\'t support them',
    ],
    correctIndex: 2,
    explanation: 'Merged cells break the grid structure. Functions like VLOOKUP, FILTER, and QUERY expect one value per cell. Merged cells cause errors, wrong results, or blank returns.',
  },
  {
    question: 'Which is better: putting notes next to data in the same row, or a separate notes column?',
    options: [
      'Notes next to data, it\'s more convenient',
      'A separate notes column, to keep the structure clean',
      'It doesn\'t matter',
      'Notes should go in a separate sheet entirely',
    ],
    correctIndex: 1,
    explanation: 'If you need notes, add a dedicated "Notes" column. Never jam them into a data column or random adjacent cells -- it breaks filtering and sorting.',
  },
  {
    question: 'A table has columns: Date, Offer ID, Clicks. Each row shows clicks for one offer on one date. What is the "record"?',
    context: {
      headers: ['Date', 'Offer ID', 'Clicks'],
      rows: [
        ['2024-01-15', 'offer_123', '1,250'],
        ['2024-01-15', 'offer_456', '890'],
        ['2024-01-16', 'offer_123', '1,100'],
      ],
    },
    options: [
      'The entire Clicks column',
      'One offer\'s clicks for one specific date',
      'All the dates in the table',
      'The header row',
    ],
    correctIndex: 1,
    explanation: 'Each row is one record. In this case, a record is one offer\'s click count for one specific date -- e.g., offer_123 had 1,250 clicks on Jan 15.',
  },
  {
    question: 'Why should you avoid blank rows in the middle of your data?',
    options: [
      'They waste storage space',
      'They make the sheet look unprofessional',
      'They break ranges, filters, and formulas that expect continuous data',
      'Google Sheets will automatically delete them',
    ],
    correctIndex: 2,
    explanation: 'Blank rows interrupt the data range. Functions like VLOOKUP stop at the first blank row, filters skip them unpredictably, and auto-detected ranges won\'t include data below the gap.',
  },
  {
    question: 'What is a "field" in a table?',
    options: [
      'A single cell',
      'A column -- the same piece of information for every record',
      'A row of data',
      'The name of the spreadsheet',
    ],
    correctIndex: 1,
    explanation: 'A field is a column. It holds one specific type of information (like "Clicks" or "Date") across all records. Every row has a value for that field.',
  },
  {
    question: 'A colleague puts a "TOTAL" row at the bottom of the data table. Why is this a problem?',
    context: {
      headers: ['Date', 'Offer ID', 'Clicks'],
      rows: [
        ['2024-01-15', 'offer_123', '1,250'],
        ['2024-01-15', 'offer_456', '890'],
        ['2024-01-16', 'offer_123', '1,100'],
        ['TOTAL', '', '3,240'],
      ],
    },
    options: [
      'Totals are never useful',
      'The total row gets included in filters, sorts, and lookups as if it were a data row',
      'Google Sheets can\'t calculate totals',
      'It makes the sheet too long',
    ],
    correctIndex: 1,
    explanation: 'A total row mixed into the data table will be treated as a regular record by formulas, filters, and sorts. It can inflate sums (double-counting), appear in filtered views, or cause VLOOKUP to return the wrong value. Keep summary rows outside the data range.',
  },
  {
    question: 'Why is "one type of data per column" an important rule?',
    options: [
      'It makes the spreadsheet load faster',
      'It prevents formulas from mixing up different data types and returning errors',
      'Google Sheets enforces this automatically',
      'It\'s just a personal preference, not a real rule',
    ],
    correctIndex: 1,
    explanation: 'If a column mixes text and numbers, or stores sign-ups and sales in the same column, formulas like SUM or AVERAGE will either error out or give misleading results. Keeping one data type per column ensures formulas work predictably.',
  },
  {
    question: 'Which date format issue would cause problems in a data table?',
    context: {
      headers: ['Date', 'Offer ID', 'Clicks'],
      rows: [
        ['Jan 15', 'offer_123', '1,250'],
        ['2024-01-15', 'offer_456', '890'],
        ['15/01/24', 'offer_123', '1,100'],
        ['16 Jan 2024', 'offer_456', '950'],
      ],
    },
    options: [
      'Using YYYY-MM-DD format',
      'Using the same format consistently throughout',
      'Mixing formats like "Jan 15", "2024-01-15", and "15/01/24" in the same column',
      'Storing dates in their own column',
    ],
    correctIndex: 2,
    explanation: 'Inconsistent date formats mean some cells are treated as text and others as actual dates. Sorting, filtering, and date-based formulas will break or produce wrong results. Pick one format and stick to it.',
  },
  {
    question: 'Why should you use Offer ID instead of Offer Name as an identifier?',
    options: [
      'Offer IDs are shorter to type',
      'Names can have typos, duplicates, and inconsistencies -- IDs are unique and stable',
      'Google Sheets requires numeric IDs',
      'It doesn\'t matter, either works fine',
    ],
    correctIndex: 1,
    explanation: 'Offer names are prone to typos (e.g., "Zippi Home Loan" vs "Zippi  Home Loan"), capitalisation differences, and aren\'t guaranteed to be unique. Offer IDs are stable, unique identifiers that formulas can match reliably.',
  },
  {
    question: 'What happens when you try to sort a table that has a title row above the headers?',
    options: [
      'The sort works perfectly',
      'The title gets sorted into the data, and the real headers may end up in the wrong position',
      'Google Sheets ignores the title row automatically',
      'The entire sheet is deleted',
    ],
    correctIndex: 1,
    explanation: 'Sort expects the first row of the selected range to be headers. If there\'s a title row above, the sort may include it as data, shuffling your headers and title into the wrong rows. Always start with headers in row 1.',
  },
]

const TABLE_RULES = [
  { rule: 'Headers in row 1, always', icon: '1' },
  { rule: 'No blank rows or columns in the data', icon: '2' },
  { rule: 'No merged cells', icon: '3' },
  { rule: 'One type of data per column', icon: '4' },
  { rule: 'Every row is a complete record', icon: '5' },
]

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

/* ─── Section 1: Good vs Bad Table ─── */
function SectionGoodTable() {
  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">What Makes a Good Table?</h2>
      <p className="mb-6 text-gray-600">
        The difference between a spreadsheet that works and one that breaks comes down to
        <strong> structure</strong>. Compare these two versions of the same data.
      </p>

      {/* Bad table */}
      <div className="mb-3 flex items-center gap-2">
        <XCircle className="h-5 w-5 text-red-500" />
        <span className="text-sm font-semibold text-red-700">
          Messy, unstructured -- formulas will break
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border-2 border-red-200">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="bg-gray-100 border border-gray-200 px-2 py-1 text-center text-xs text-gray-400 w-8" />
              {['A', 'B', 'C', 'D', 'E', 'F'].map((col) => (
                <th key={col} className="bg-gray-100 border border-gray-200 px-2 py-1 text-center text-xs font-bold text-gray-400">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BAD_CELLS.map((row, ri) => (
              <tr key={ri} className={ri === 1 ? 'bg-red-50/50' : ''}>
                <td className="bg-gray-100 border border-gray-200 px-2 py-2 text-center text-xs font-bold text-gray-400 w-8">
                  {ri + 1}
                </td>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`border border-gray-200 px-3 py-2 ${
                      ri === 0 && ci === 0
                        ? 'bg-red-50 font-bold text-red-800'
                        : ri === 2
                          ? 'bg-gray-100 font-semibold text-gray-700'
                          : ri === 8
                            ? 'bg-amber-50 font-semibold text-amber-800'
                            : cell === ''
                              ? 'bg-red-50/30'
                              : 'text-gray-600'
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {[
          'Header row takes up row 1 instead of column names',
          'Blank row 2 breaks ranges and filters',
          'Missing values in Date and Offer Name',
          'TOTAL row mixed into the data',
          'Random notes jammed into cells',
          'Inconsistent date formats (Jan 15, 2024-01-15, 16 Jan 2024, 15/01/24)',
          'No Offer ID — offer name is a bad primary key (names change, have typos, and aren\'t unique across networks)',
          'Conversion columns inconsistent — some offers report Signups, others report Sales',
        ].map((issue) => (
          <div key={issue} className="flex items-start gap-2 text-sm text-red-600">
            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {issue}
          </div>
        ))}
      </div>

      {/* Good table */}
      <div className="mt-8 mb-3 flex items-center gap-2">
        <CheckCircle className="h-5 w-5 text-emerald-500" />
        <span className="text-sm font-semibold text-emerald-700">
          Clean, structured -- formulas will work reliably
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border-2 border-emerald-200">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="bg-gray-100 border border-gray-200 px-2 py-1 text-center text-xs text-gray-400 w-8" />
              {['A', 'B', 'C', 'D', 'E'].map((col) => (
                <th key={col} className="bg-gray-100 border border-gray-200 px-2 py-1 text-center text-xs font-bold text-gray-400">
                  {col}
                </th>
              ))}
            </tr>
            <tr className="bg-emerald-50">
              <td className="bg-gray-100 border border-gray-200 px-2 py-2.5 text-center text-xs font-bold text-gray-400 w-8">1</td>
              {GOOD_HEADERS.map((h) => (
                <th key={h} className="border border-gray-200 px-4 py-2.5 text-left font-semibold text-gray-700">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GOOD_ROWS.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="bg-gray-100 border border-gray-200 px-2 py-2 text-center text-xs font-bold text-gray-400 w-8">
                  {i + 2}
                </td>
                {row.map((cell, j) => (
                  <td key={j} className="border border-gray-200 px-4 py-2 text-gray-600">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {[
          'Headers in row 1',
          'No blank rows',
          'Every cell has a value',
          'Consistent date format',
          'One data type per column',
          'No totals mixed into data',
        ].map((point) => (
          <div key={point} className="flex items-start gap-2 text-sm text-emerald-600">
            <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {point}
          </div>
        ))}
      </div>
    </motion.section>
  )
}

/* ─── Section 2: Rows = Records, Columns = Fields ─── */
function SectionRecordsFields() {
  const [highlight, setHighlight] = useState(null) // { type: 'row', index } or { type: 'col', index }

  const headers = ['Date', 'Offer ID', 'Clicks', 'Conversions', 'Conv. Rate']
  const rows = [
    ['2024-01-15', 'offer_123', '1,250', '45', '3.6%'],
    ['2024-01-15', 'offer_456', '890', '12', '1.3%'],
    ['2024-01-16', 'offer_123', '1,100', '38', '3.5%'],
    ['2024-01-16', 'offer_456', '950', '15', '1.6%'],
  ]

  const recordDesc = highlight?.type === 'row'
    ? `Record: ${rows[highlight.index][1]} on ${rows[highlight.index][0]} -- ${rows[highlight.index][2]} clicks, ${rows[highlight.index][3]} conversions (${rows[highlight.index][4]})`
    : null

  const fieldDesc = highlight?.type === 'col'
    ? `Field: ${headers[highlight.index]} -- the same piece of information for every record`
    : null

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">Rows = Records, Columns = Fields</h2>
      <p className="mb-6 text-gray-600">
        Click a <strong>row</strong> to see it as a record, or click a{' '}
        <strong>column header</strong> to see it as a field.
      </p>

      {/* Info bar */}
      <AnimatePresence mode="wait">
        {(recordDesc || fieldDesc) && (
          <motion.div
            key={recordDesc || fieldDesc}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`mb-4 rounded-lg border p-3 text-sm font-medium ${
              highlight?.type === 'row'
                ? 'border-blue-200 bg-blue-50 text-blue-800'
                : 'border-purple-200 bg-purple-50 text-purple-800'
            }`}
          >
            {recordDesc || fieldDesc}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {headers.map((h, ci) => (
                <th
                  key={h}
                  onClick={() =>
                    setHighlight(
                      highlight?.type === 'col' && highlight.index === ci
                        ? null
                        : { type: 'col', index: ci }
                    )
                  }
                  className={`cursor-pointer border border-gray-200 px-4 py-2.5 text-left font-semibold transition-colors ${
                    highlight?.type === 'col' && highlight.index === ci
                      ? 'bg-purple-100 text-purple-800 ring-2 ring-purple-400 ring-inset'
                      : 'bg-gray-100 text-gray-700 hover:bg-purple-50'
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr
                key={ri}
                onClick={() =>
                  setHighlight(
                    highlight?.type === 'row' && highlight.index === ri
                      ? null
                      : { type: 'row', index: ri }
                  )
                }
                className="cursor-pointer"
              >
                {row.map((cell, ci) => {
                  const isRowHL = highlight?.type === 'row' && highlight.index === ri
                  const isColHL = highlight?.type === 'col' && highlight.index === ci
                  return (
                    <td
                      key={ci}
                      className={`border border-gray-200 px-4 py-2.5 transition-colors ${
                        isRowHL
                          ? 'bg-blue-100 text-blue-800 font-medium'
                          : isColHL
                            ? 'bg-purple-50 text-purple-800 font-medium'
                            : ri % 2 === 0
                              ? 'bg-white text-gray-600'
                              : 'bg-gray-50 text-gray-600'
                      }`}
                    >
                      {cell}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="mb-1 text-sm font-bold text-blue-700">Row = Record</div>
          <p className="text-sm text-blue-600">
            Everything about one thing. One offer on one date: its clicks, conversions, and rate.
          </p>
        </div>
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
          <div className="mb-1 text-sm font-bold text-purple-700">Column = Field</div>
          <p className="text-sm text-purple-600">
            The same piece of information for every record. E.g., the "Clicks" column holds the click count for all records.
          </p>
        </div>
      </div>
    </motion.section>
  )
}

/* ─── Section 3: Why Structure Matters ─── */
function SectionWhyStructure() {
  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">Why Structure Matters</h2>
      <p className="mb-6 text-gray-600">
        Formulas can only work reliably on <strong>clean, structured tables</strong>. If your data is
        messy, every formula you write will be fragile and break when data changes.
      </p>

      {/* Messy vs Clean comparison */}
      <div className="mb-8 grid gap-6 md:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="rounded-xl border-2 border-red-200 bg-red-50/50 p-5"
        >
          <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-red-700">
            <XCircle className="h-5 w-5" /> With messy data
          </h3>
          <div className="space-y-2 text-sm text-red-700">
            <p className="rounded bg-red-100 p-2 font-mono text-xs">
              =VLOOKUP("offer_123", A:C, 3, FALSE)
            </p>
            <p>
              This might return the wrong row, skip blanks, or crash entirely because VLOOKUP
              expects a clean first column with no gaps.
            </p>
            <p className="font-semibold">
              Result: #N/A errors, wrong numbers, hours of debugging.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-5"
        >
          <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-emerald-700">
            <CheckCircle className="h-5 w-5" /> With clean data
          </h3>
          <div className="space-y-2 text-sm text-emerald-700">
            <p className="rounded bg-emerald-100 p-2 font-mono text-xs">
              =VLOOKUP("offer_123", A:C, 3, FALSE)
            </p>
            <p>
              Every row has a value, columns are consistent, no blanks to trip over. The formula
              finds the right match every time.
            </p>
            <p className="font-semibold">
              Result: Correct number, every time, no surprises.
            </p>
          </div>
        </motion.div>
      </div>

      {/* The 5 Rules */}
      <h3 className="mb-4 text-lg font-semibold text-gray-800">The 5 Rules of Table Structure</h3>
      <div className="space-y-3">
        {TABLE_RULES.map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
              {item.icon}
            </div>
            <p className="font-medium text-gray-700">{item.rule}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4"
      >
        <p className="text-sm text-amber-800">
          <strong>Think of it this way:</strong> your spreadsheet is a mini database. Databases have
          strict rules about structure for a reason -- it's the only way to guarantee formulas and
          queries return the right answer.
        </p>
      </motion.div>
    </motion.section>
  )
}

/* ─── Main Module Component ─── */
export default function Module2() {
  return (
    <ModuleLayout moduleId={2}>
      <div className="space-y-10">
        <SectionGoodTable />
        <SectionRecordsFields />
        <SectionWhyStructure />
        <Quiz moduleId={2} questions={QUIZ} />
      </div>
    </ModuleLayout>
  )
}
